import { ApolloClient, ApolloLink, InMemoryCache, HttpLink } from '@apollo/client';
import type { DeepPartial } from '@apollo/client/utilities';
import { ApolloProvider } from '@apollo/client/react';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import * as Sentry from '@sentry/browser';
import { useSnackbar } from 'notistack';
import React, { useCallback, useMemo } from 'react';
import { createNetworkStatusNotifier } from 'react-apollo-network-status';
import { useTranslation } from 'react-i18next';
import { useSignOut } from 'src/api/provider';
import { Maybe } from 'src/graphql/lib';
import Cookies from 'universal-cookie';
import 'whatwg-fetch';
import { API_HOST, CSRF_COOKIE_NAME } from '../constants/environment';
import { ACCESS_TOKEN_KEY, IMPERSONATED_KEY } from '../constants/sessionStorage';
import { FieldErrors } from './errorHandling';

const hasStatusCode = (value: unknown): value is { statusCode: number } =>
    typeof value === 'object' && value !== null && 'statusCode' in value;
const cookies = new Cookies();

function useApolloClient(
    networkStatusNotifierLink: ReturnType<typeof createNetworkStatusNotifier>['link'],
) {
    const { t } = useTranslation('validation');
    const { enqueueSnackbar } = useSnackbar();
    const signOut = useSignOut();

    // useCallback required - showError is in useMemo deps for ApolloClient creation.
    // Without stable reference, Apollo client recreates every render → infinite loop.
    const showError = useCallback(
        (message: string | string[]) => {
            if (Array.isArray(message)) {
                message = message.join(' ');
            }
            enqueueSnackbar(message, { variant: 'error' });
        },
        [enqueueSnackbar],
    );

    // Keep useMemo here - ApolloClient is a third-party library that requires stable reference
    const apolloClient = useMemo(() => {
        const cache = new InMemoryCache({
            possibleTypes: {},
        });

        const httpLink = new HttpLink({
            uri: `${API_HOST}/api/graphql/`,
            credentials: 'include',
            fetch: window.fetch,
        });

        const customHeadersLink = setContext((_, { headers }) => {
            const customHeaders: { [key: string]: string } = {
                'X-CSRFToken': cookies.get(CSRF_COOKIE_NAME),
            };
            const impersonated = sessionStorage.getItem(IMPERSONATED_KEY);
            if (impersonated) {
                customHeaders['X-Impersonate'] = impersonated;
            }
            const accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
            if (accessToken) {
                customHeaders['Authorization'] = `Bearer ${accessToken}`;
            }
            return {
                headers: {
                    ...headers,
                    ...customHeaders,
                },
                fetchOptions: {
                    referrerPolicy: 'no-referrer-when-downgrade',
                },
            };
        });

        const sentryBreadcrumbLink = new ApolloLink((operation, forward) => {
            Sentry.addBreadcrumb({
                category: 'gql',
                level: 'info',
                message: `Operation: ${operation.operationName}`,
            });

            return forward?.(operation) ?? null;
        });

        const errorLink = onError(({ error, operation }) => {
            if (!error) {
                return;
            }

            if (CombinedGraphQLErrors.is(error)) {
                error.errors.forEach((graphQLError) => {
                    if (!graphQLError.extensions) {
                        showError(graphQLError.message);
                        return;
                    }
                    switch (graphQLError.extensions.code) {
                        case 'ValidationError':
                            const errorFields = graphQLError.extensions.fields as FieldErrors;
                            if ('__all__' in errorFields) {
                                showError(errorFields.__all__);
                            } else {
                                const errorMessage = Object.values(errorFields).flatMap(
                                    (error) => error,
                                );
                                showError(errorMessage || t('validationErrorMessage'));
                            }
                            break;
                        case 'AuthorizationError':
                            showError(t('unauthorizedErrorMessage'));
                            break;
                        case 'InternalServerError':
                        default:
                            showError(graphQLError.message);
                    }
                });
                return;
            }

            if (!hasStatusCode(error)) {
                showError(t('unknownErrorMessage'));
                return;
            }

            const errorWithStatus = error;

            if ([401, 403].includes(errorWithStatus.statusCode)) {
                if (operation.operationName === 'GetMe') {
                    return;
                }
                if (operation.operationName === 'GetFeatureFlags') {
                    return;
                }
                if (window.location.pathname === '/login') {
                    return;
                }
                const redirectTo = sessionStorage.getItem(ACCESS_TOKEN_KEY)
                    ? '/invalid-link'
                    : undefined;
                signOut(redirectTo);
                return;
            }

            if (errorWithStatus.statusCode >= 400 && errorWithStatus.statusCode < 500) {
                Sentry.captureException(errorWithStatus);
            }

            showError(t('unknownErrorMessage'));
        });

        return new ApolloClient({
            cache,

            link: ApolloLink.from([
                networkStatusNotifierLink,
                errorLink,
                customHeadersLink,
                sentryBreadcrumbLink,
                httpLink,
            ]),

            defaultOptions: {
                watchQuery: {
                    errorPolicy: 'all',
                    fetchPolicy: 'cache-and-network',
                    nextFetchPolicy: (lastFetchPolicy) => {
                        if (
                            lastFetchPolicy === 'cache-and-network' ||
                            lastFetchPolicy === 'network-only'
                        ) {
                            return 'cache-first';
                        }
                        return lastFetchPolicy;
                    },
                },
            },
        });
    }, [showError, t, networkStatusNotifierLink, signOut]);

    return apolloClient;
}

export interface GraphqlProviderProps {
    networkStatusNotifierLink: ReturnType<typeof createNetworkStatusNotifier>['link'];
    children: React.ReactNode;
}

export const GraphqlProvider = ({ networkStatusNotifierLink, children }: GraphqlProviderProps) => {
    const client = useApolloClient(networkStatusNotifierLink);
    return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export type Node = {
    id: string;
};

type Edge<TNode extends Node> = {
    node: Maybe<TNode>;
};

export type Connection<TNode extends Node> = {
    edges: Maybe<Edge<TNode>>[];
};

export type DeepPartialConnection<TNode extends Node> = DeepPartial<Connection<TNode>>;

export function getNodesFromConnection<TNode extends Node>(
    connection: Connection<TNode> | DeepPartialConnection<TNode> | null | undefined,
) {
    if (!connection) {
        return null;
    }
    const edges = (connection.edges ?? []) as (Edge<TNode> | null | undefined)[];
    return edges
        .map((edge) => edge?.node as TNode | null | undefined)
        .filter((node): node is TNode => Boolean(node));
}
